/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect, useMemo } from "react"
import { EmotionCache } from "@emotion/cache"
import { RouteType } from "./routes"

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom"

// @mui material components
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"

// Material Dashboard 2 React example components
import Sidenav from "pages/components/Sidenav"
import Configurator from "pages/components/Configurator"

// Material Dashboard 2 React themes
import theme from "assets/theme"
import themeRTL from "assets/theme/theme-rtl"

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark"
import themeDarkRTL from "assets/theme-dark/theme-rtl"

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl"
import { CacheProvider } from "@emotion/react"
import createCache from "@emotion/cache"

// Material Dashboard 2 React routes
import routes from "routes"

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context"

// Loading Context
import { LoadingProvider, useLoading } from "./pages/LoadingContext"

// Connection Error Context
import { ConnectionErrorProvider, useConnectionError } from "./pages/ConnectionErrorContext"
import Card from "@mui/material/Card"
import MDTypography from "components/MDTypography"

import "./pages/LoadingContext.css"
import "./pages/ManageCommon.css"

// Images
import brandWhite from "assets/images/logo.png"
import brandDark from "assets/images/logo.png"

// ローディングオーバーレイコンポーネント
import { createPortal } from "react-dom"
const LoadingOverlay: React.FC = () => {
  const { isLoading } = useLoading()
  return isLoading
    ? createPortal(
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>,
        document.body
      )
    : null
}

// 通信エラーオーバーレイコンポーネント
const ConnectionErrorOverlay: React.FC = () => {
  const { isConnectionError } = useConnectionError()
  const { showConnectionError } = useConnectionError()

  return isConnectionError ? (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "10px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div style={{ display: "flex", width: "100%", textAlign: "center" }}>
          <MDTypography variant="h5" fontWeight="medium" sx={{ width: "100%" }}>
            通信エラー
          </MDTypography>
          <button className="close-button-connection-error" onClick={() => showConnectionError(false)}>
            ×
          </button>
        </div>
        <div style={{ textAlign: "center", width: "100%", marginTop: "10px", fontSize: "0.75em" }}>
          サーバーとの接続に失敗しました
        </div>
      </Card>
    </div>
  ) : null
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController()
  const { miniSidenav, direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } = controller
  const [onMouseEnter, setOnMouseEnter] = useState(false)
  const [rtlCache, setRtlCache] = useState<EmotionCache | null>(null)
  const { pathname } = useLocation()

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin]
    })

    setRtlCache(cacheRtl)
  }, [])

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false)
      setOnMouseEnter(true)
    }
  }

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true)
      setOnMouseEnter(false)
    }
  }

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator)

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction)
  }, [direction])

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0
    }
  }, [pathname])

  const getRoutes = (allRoutes: RouteType[]): React.ReactNode[] =>
    allRoutes.map((route: RouteType): React.ReactNode => {
      if (route.collapse) {
        return getRoutes(route.collapse)
      }

      if (route.route) {
        return <Route path={route.route} element={route.component} key={route.key} />
      }

      return null
    })

  return (
    <LoadingProvider>
      <ConnectionErrorProvider>
        <LoadingOverlay />
        <ConnectionErrorOverlay />
        {direction === "rtl" ? (
          <CacheProvider value={rtlCache!}>
            <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
              <CssBaseline />
              {layout === "dashboard" && (
                <>
                  <Sidenav
                    color={sidenavColor}
                    brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                    brandName="Material Dashboard 2"
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                  <Configurator />
                </>
              )}
              {layout === "vr" && <Configurator />}
              <Routes>
                {getRoutes(routes)}
                <Route path="*" element={<Navigate to="/home" />} />
              </Routes>
            </ThemeProvider>
          </CacheProvider>
        ) : (
          <ThemeProvider theme={darkMode ? themeDark : theme}>
            <CssBaseline />
            {layout === "dashboard" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={brandWhite}
                  brandName="SYNCLAYER Sample"
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator />
              </>
            )}
            {layout === "vr" && <Configurator />}
            {/* デフォルトページを、loginページにする */}
            <Routes>
              {getRoutes(routes)}
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          </ThemeProvider>
        )}
      </ConnectionErrorProvider>
    </LoadingProvider>
  )
}
